import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {InventAuctionLot, AuctionLotStatus, InventUserDTO} from '../../../../../interfaces/auction-import';
import {VehicleAuctionImportService} from '../../../../../services/vehicle-auction-import.service';
import {SearchResultDTO, User} from '../../../../../global/interfaces';
import {UserService} from '../../../../../global/services';
import {ModalDirective, TabsetComponent} from 'ng-uikit-pro-standard';
import {AdminUrlService} from "../../../services";
import {Location} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {URLService, AdvertService} from "../../../../../services";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-vehicle-management-dashboard',
  templateUrl: './vehicle-management-dashboard.component.html',
  styleUrls: ['./vehicle-management-dashboard.component.scss']
})
export class VehicleManagementDashboardComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('tabs') uiTabs: TabsetComponent;

  // Auction lots data
  vehicles: InventAuctionLot[] = [];
  inventUsers: InventUserDTO[] = [];
  selectedVehicles: Set<string> = new Set<string>();
  importingVehicles: Set<string> = new Set<string>(); // Track vehicles being imported
  restoringVehicles: Set<string> = new Set<string>(); // Track vehicles being restored

  // Make enum available to template
  AuctionLotStatus = AuctionLotStatus;

  // Filter state
  activeTab: 'ReadyToImport' | 'Imported' | 'RecentlySold' | 'AllSold' | 'Removed' = 'ReadyToImport';
  searchTerm = '';
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  selectedInventUserId: string | null = null;

  // Server-side pagination
  currentPage = 1;
  pageSize = 30;
  totalItems = 0;

  // Make Math available to the template
  Math = Math;

  // Loading state
  loading = false;
  importing = false;

  // Modal state
  showImportModal = false;
  showRemoveModal = false;
  showActionModal = false;
  showWithdrawModal = false;
  showRestoreModal = false;
  showReservePriceModal = false;
  vehicleForModal: InventAuctionLot | null = null;
  editedReservePrice: number | null = null;
  currentImageIndex = 0;
  user: User;

  tabTypes = {
    0: 'ReadyToImport',
    1: 'Imported',
    2: 'RecentlySold',
    3: 'AllSold',
    4: 'Removed'
  };

  @ViewChild('vehicleDetailsModal') vehicleDetailsModal: ModalDirective;
  selectedTab: any;
  private routeSubscription!: Subscription;
  adding = false;

  constructor(
    private vehicleImportService: VehicleAuctionImportService,
    private adminURLService: AdminUrlService,
    private userService: UserService,
    private location: Location,
    private route: ActivatedRoute,
    private advertService: AdvertService,
    private urlService: URLService
  ) {

    const urlTabIndex: number = parseInt(this.route.snapshot.params.tab) || 0;
    this.selectedTab = urlTabIndex;

    // Set the initial active tab based on URL
    if (this.tabTypes[urlTabIndex]) {
      this.activeTab = this.tabTypes[urlTabIndex];
    }

  }

  async ngOnInit(): Promise<void> {
    await this.userService.loadCurrentUser().then(() => {
      this.user = this.userService.CurrentUser;
    });

    // Load invent users for dropdown
    await this.loadInventUsers();

    // Subscribe to route parameter changes
    this.routeSubscription = this.route.params.subscribe(params => {
      const tabIndex = parseInt(params.tab) || 0;
      this.selectedTab = tabIndex;
      if (this.tabTypes[tabIndex]) {
        this.activeTab = this.tabTypes[tabIndex];
      }
      // Set the UI tab after a delay to ensure component is ready
      setTimeout(() => {
        if (this.uiTabs) {
          this.uiTabs.setActiveTab(tabIndex + 1);
        }
      }, 200);
    });

    // Load vehicles for the initial tab
    this.loadVehicles().then();
  }

  ngAfterViewInit() {
    // Set the active tab based on URL parameter
    setTimeout(() => {
      if (this.selectedTab !== undefined && this.selectedTab !== null && this.uiTabs) {
        // The UI tabs component uses 1-based indexing
        this.uiTabs.setActiveTab(this.selectedTab + 1);
      }
    }, 100); // Give the component time to initialize
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  /**
   * Format currency with the appropriate symbol
   */
  formatCurrency(value: number | null): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Load invent users for admin dropdown
   */
  async loadInventUsers(): Promise<void> {
    try {
      this.inventUsers = await this.vehicleImportService.getInventUsers();
      if (this.inventUsers && this.inventUsers.length > 0) {
        this.selectedInventUserId = this.inventUsers[0].id;
      }
    } catch (error) {
      console.error('Error loading invent users:', error);
    }
  }

  importInventVehicles() {
    try {
      // begin the import process
      this.importing = true;

      this.vehicleImportService.importInventData(this.selectedInventUserId).then(() => {
        this.loadVehicles();
      });
    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      this.importing = false;
    }
  }

  async loadVehicles(): Promise<void> {
    try {
      this.loading = true;

      // Calculate server-side pagination parameters
      const offset = (this.currentPage - 1) * this.pageSize;
      const limit = this.pageSize;

      // Get search parameters
      const searchTerm = this.searchTerm || undefined;
      const dateFrom = this.dateFrom || undefined;
      const dateTo = this.dateTo || undefined;

      let response: SearchResultDTO;

      switch (this.activeTab) {
        case 'ReadyToImport':
          response = await this.vehicleImportService.getAvailableLots(this.selectedInventUserId, offset, limit, searchTerm);
          break;
        case 'Imported':
          response = await this.vehicleImportService.getImportedVehicles(this.selectedInventUserId, offset, limit, searchTerm, dateFrom, dateTo);
          break;
        case 'RecentlySold':
          response = await this.vehicleImportService.getSoldVehicles(this.selectedInventUserId, true, offset, limit, searchTerm, dateFrom, dateTo);
          break;
        case 'AllSold':
          response = await this.vehicleImportService.getSoldVehicles(this.selectedInventUserId, false, offset, limit, searchTerm, dateFrom, dateTo);
          break;
        case 'Removed':
          response = await this.vehicleImportService.getRemovedVehicles(this.selectedInventUserId, offset, limit, searchTerm);
          break;
        default:
          response = {results: [], totalItems: 0};
      }

      // Update component state with response data
      this.vehicles = response.results;
      this.totalItems = response.totalItems;

      console.log("Vehicles loaded:", this.vehicles.length, "Total:", this.totalItems);

    } catch (error) {
      console.error('Error loading vehicles:', error);
    } finally {
      this.loading = false;
    }
  }

  applyFilters(): void {
    this.currentPage = 1; // Reset to first page when filters change
    this.selectedVehicles.clear(); // Clear selections when filtering
    this.loadVehicles();
  }

  setActiveTab(tab: 'ReadyToImport' | 'Imported' | 'RecentlySold' | 'AllSold' | 'Removed'): void {
    this.activeTab = tab;
    this.selectedVehicles.clear();
    this.currentPage = 1;
    this.loadVehicles();
  }

  /**
   * Handle invent user selection change (for admin users)
   */
  onInventUserSelectionChange(inventUserId: string | null): void {
    this.selectedInventUserId = inventUserId;
    this.currentPage = 1;
    this.selectedVehicles.clear();
    this.loadVehicles();
  }

  onFiltersChanged(filters: { searchTerm: string; dateFrom: string | null; dateTo: string | null }): void {
    // Update filter values
    this.searchTerm = filters.searchTerm || '';
    this.dateFrom = filters.dateFrom ? new Date(filters.dateFrom) : null;
    this.dateTo = filters.dateTo ? new Date(filters.dateTo) : null;

    // Apply filters to reload vehicles
    this.applyFilters();
  }

  // Server-side pagination helper methods
  get totalPages(): number {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  getStartRecord(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  getEndRecord(): number {
    const end = this.currentPage * this.pageSize;
    return Math.min(end, this.totalItems);
  }

  getVisiblePageNumbers(): (number | string)[] {
    const maxVisiblePages = 7;
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      return Array.from({length: totalPages}, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // Always show first page
    pages.push(1);

    // Calculate range around current page
    let startPage = Math.max(2, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pages.push('...');
    }

    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page if more than 1 page
    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.selectedVehicles.clear(); // Clear selections when changing pages
      this.loadVehicles();
    }
  }

  toggleVehicleSelection(vehicleId: string): void {
    if (this.selectedVehicles.has(vehicleId)) {
      this.selectedVehicles.delete(vehicleId);
    } else {
      this.selectedVehicles.add(vehicleId);
    }
  }

  areAllOnPageSelected(): boolean {
    return this.vehicles.length > 0 && this.vehicles.every(v => this.selectedVehicles.has(v.id));
  }

  selectAllOnPage(): void {
    const allSelected = this.areAllOnPageSelected();

    if (allSelected) {
      // Deselect all on page
      this.vehicles.forEach(v => this.selectedVehicles.delete(v.id));
    } else {
      // Select all on page
      this.vehicles.forEach(v => this.selectedVehicles.add(v.id));
    }
  }

  openImportModal(vehicle?: InventAuctionLot): void {
    if (vehicle) {
      this.vehicleForModal = vehicle;
    }
    this.showImportModal = true;
  }

  openRemoveModal(vehicle?: InventAuctionLot): void {
    if (vehicle) {
      this.vehicleForModal = vehicle;
    }
    this.showRemoveModal = true;
  }

  openActionModal(vehicle: InventAuctionLot): void {
    this.vehicleForModal = vehicle;
    this.showActionModal = true;
  }

  openWithdrawModal(vehicle?: InventAuctionLot): void {
    if (vehicle) {
      this.vehicleForModal = vehicle;
    }
    this.showWithdrawModal = true;
  }

  openRestoreModal(vehicle?: InventAuctionLot): void {
    if (vehicle) {
      this.vehicleForModal = vehicle;
    }
    this.showRestoreModal = true;
  }

  openVehicleDetailsModal(vehicle: InventAuctionLot): void {
    this.vehicleForModal = vehicle;
    this.currentImageIndex = 0; // Reset to first image
    this.vehicleDetailsModal.show();
  }

  getVehicleImages(): string[] {
    if (!this.vehicleForModal) return [];

    // If imageUrls array exists and has items, use it
    if (this.vehicleForModal.imageUrls && this.vehicleForModal.imageUrls.length > 0) {
      return this.vehicleForModal.imageUrls;
    }

    // Otherwise, use defaultImageUrl if it exists
    if (this.vehicleForModal.defaultImageUrl) {
      return [this.vehicleForModal.defaultImageUrl];
    }

    // Return empty array if no images
    return [];
  }

  nextImage(): void {
    const images = this.getVehicleImages();
    if (this.currentImageIndex < images.length - 1) {
      this.currentImageIndex++;
    }
  }

  previousImage(): void {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
    }
  }

  setCurrentImage(index: number): void {
    this.currentImageIndex = index;
  }

  onImageError(event: any): void {
    // Set a fallback image when image fails to load
    //event.target.src = 'assets/images/vehicle-placeholder.jpg';
  }

  /**
   * Check if a vehicle is currently being imported
   */
  isVehicleImporting(vehicleId: string): boolean {
    return this.importingVehicles.has(vehicleId);
  }

  /**
   * Check if a vehicle is currently being restored
   */
  isVehicleRestoring(vehicleId: string): boolean {
    return this.restoringVehicles.has(vehicleId);
  }

  /**
   * Import a single vehicle directly (used from vehicle details modal)
   */
  async importSingleVehicle(vehicle: InventAuctionLot): Promise<void> {
    const vehicleId = String(vehicle.id);

    // Mark vehicle as importing
    this.importingVehicles.add(vehicleId);

    try {
      await this.vehicleImportService.importVehicles(this.selectedInventUserId, [vehicleId]);
      // Remove from importing set when successful
      this.importingVehicles.delete(vehicleId);
      await this.loadVehicles();
      this.vehicleDetailsModal.hide();
    } catch (error) {
      console.error(`Error importing vehicle ${vehicleId}:`, error);
      // Remove from importing set even on error
      this.importingVehicles.delete(vehicleId);
    }
  }

  closeModals(): void {
    this.importing = false;
    this.showImportModal = false;
    this.showRemoveModal = false;
    this.showActionModal = false;
    this.showWithdrawModal = false;
    this.showRestoreModal = false;
    this.showReservePriceModal = false;
    this.vehicleForModal = null;
    this.editedReservePrice = null;
  }

  /**
   * Open the reserve price edit modal for a vehicle
   */
  openReservePriceModal(vehicle: InventAuctionLot): void {
    this.vehicleForModal = vehicle;
    this.editedReservePrice = vehicle.reservePrice;
    this.showReservePriceModal = true;
  }

  /**
   * Save the edited reserve price
   */
  async saveReservePrice(): Promise<void> {
    if (!this.vehicleForModal) return;

    try {
      // Update the vehicle's reserve price
      await this.vehicleImportService.updateReservePrice(
        this.vehicleForModal.id,
        this.editedReservePrice
      );

      // Update the local vehicle data
      const updatedVehicleIndex = this.vehicles.findIndex(v => v.id === this.vehicleForModal?.id);
      if (updatedVehicleIndex !== -1) {
        this.vehicles[updatedVehicleIndex].reservePrice = this.editedReservePrice;
      }

      // Close the modal
      this.closeModals();
    } catch (error) {
      console.error('Error updating reserve price:', error);
      // You could add error handling/display here
    }
  }

  async importVehicles(): Promise<void> {
    let inventLotIds: string[] = [];
    this.importing = true;

    // Determine which lot IDs to import
    if (this.vehicleForModal) {
      inventLotIds = [String(this.vehicleForModal.id)];
    } else {
      // Convert each value to string to ensure we have consistent string GUIDs
      inventLotIds = Array.from(this.selectedVehicles).map(id => String(id));
    }

    // Mark vehicles as importing
    inventLotIds.forEach(id => this.importingVehicles.add(id));

    try {
      // Process vehicles sequentially
      for (const vehicleId of inventLotIds) {
        try {
          await this.vehicleImportService.importVehicles(this.selectedInventUserId, [vehicleId]);
          // Remove from importing set when successful
          this.importingVehicles.delete(vehicleId);
        } catch (error) {
          console.error(`Error importing vehicle ${vehicleId}:`, error);
          // Remove from importing set even on error
          this.importingVehicles.delete(vehicleId);
          // Continue with next vehicle instead of stopping the entire process
        }
      }

      this.selectedVehicles.clear();
      await this.loadVehicles();
    } catch (error) {
      console.error('Error importing vehicles:', error);
    } finally {
      // Ensure all vehicles are removed from importing set
      inventLotIds.forEach(id => this.importingVehicles.delete(id));
      this.closeModals();
    }
  }

  async removeVehicles(): Promise<void> {
    const vehiclesToRemove = this.vehicleForModal
      ? [this.vehicleForModal.id]
      : Array.from(this.selectedVehicles);

    try {
      this.loading = true;
      await this.vehicleImportService.removeVehicles(vehiclesToRemove);
      this.selectedVehicles.clear();
      await this.loadVehicles();
    } catch (error) {
      console.error('Error removing vehicles:', error);
    } finally {
      this.loading = false;
      this.closeModals();
    }
  }

  async markAsActioned(): Promise<void> {
    if (!this.vehicleForModal) {
      return;
    }

    try {
      this.loading = true;
      await this.loadVehicles();
    } catch (error) {
      console.error('Error marking vehicle as actioned:', error);
    } finally {
      this.loading = false;
      this.closeModals();
    }
  }

  async restoreVehicle(vehicle: InventAuctionLot): Promise<void> {
    try {
      this.loading = true;
      await this.vehicleImportService.restoreVehicle(this.selectedInventUserId, vehicle.id);
      await this.loadVehicles();
    } catch (error) {
      console.error('Error restoring vehicle:', error);
    } finally {
      this.loading = false;
    }
  }

// Add to component properties
  failedRestoringVehicles: Set<string> = new Set<string>(); // Track vehicles that failed to restore

  async restoreVehicles(): Promise<void> {
    let vehicleIds: string[] = [];

    // Determine which vehicle IDs to restore
    if (this.vehicleForModal) {
      vehicleIds = [String(this.vehicleForModal.id)];
    } else {
      // Convert each value to string to ensure we have consistent string GUIDs
      vehicleIds = Array.from(this.selectedVehicles).map(id => String(id));
    }

    console.log("IDs to restore: ", vehicleIds);

    // Clear any previous failed states for these vehicles
    vehicleIds.forEach(id => this.failedRestoringVehicles.delete(id));

    // Mark vehicles as restoring
    vehicleIds.forEach(id => this.restoringVehicles.add(id));

    let successCount = 0;
    let failureCount = 0;

    try {
      // Process vehicles sequentially (one by one)
      for (const vehicleId of vehicleIds) {
        try {
          console.log(`Starting restoration of vehicle ${vehicleId}...`);
          await this.vehicleImportService.restoreVehicle(this.selectedInventUserId, vehicleId);
          console.log(`Successfully restored vehicle ${vehicleId}`);

          // Remove from restoring set when successful
          this.restoringVehicles.delete(vehicleId);
          successCount++;
        } catch (error) {
          console.error(`Error restoring vehicle ${vehicleId}:`, error);

          // Move from restoring to failed (don't just remove)
          this.restoringVehicles.delete(vehicleId);
          this.failedRestoringVehicles.add(vehicleId);
          failureCount++;

          // Continue with next vehicle
        }
      }

      // Show summary message
      if (failureCount > 0) {
        console.warn(`Restoration completed: ${successCount} successful, ${failureCount} failed`);
        // You could show a toast notification here
      } else {
        console.log(`All ${successCount} vehicles restored successfully`);
      }

      this.selectedVehicles.clear();
      await this.loadVehicles();
    } catch (error) {
      console.error('Error in restoration process:', error);
    } finally {
      this.closeModals();

      // Auto-clear failed states after a delay (optional)
      if (this.failedRestoringVehicles.size > 0) {
        setTimeout(() => {
          this.failedRestoringVehicles.clear();
        }, 10000); // Clear failed indicators after 10 seconds
      }
    }
  }

  /**
   * Retry restoring a single failed vehicle
   */
  async retryRestoreVehicle(vehicle: InventAuctionLot): Promise<void> {
    const vehicleId = String(vehicle.id);

    // Clear the failed state and mark as restoring again
    this.failedRestoringVehicles.delete(vehicleId);
    this.restoringVehicles.add(vehicleId);

    try {
      console.log(`Retrying restoration of vehicle ${vehicleId}...`);
      await this.vehicleImportService.restoreVehicle(this.selectedInventUserId, vehicleId);
      console.log(`Successfully restored vehicle ${vehicleId} on retry`);

      // Remove from restoring set when successful
      this.restoringVehicles.delete(vehicleId);

      // Reload vehicles to reflect the change
      await this.loadVehicles();
    } catch (error) {
      console.error(`Error retrying restoration of vehicle ${vehicleId}:`, error);

      // Move back to failed state
      this.restoringVehicles.delete(vehicleId);
      this.failedRestoringVehicles.add(vehicleId);
    }
  }

  // Helper method for template
  isVehicleFailedRestoring(vehicleId: string): boolean {
    return this.failedRestoringVehicles.has(vehicleId);
  }

  async withdrawVehicles(): Promise<void> {
    // advertIds are used to withdraw vehicles from the platform
    // convert selected vehicles to advertIds
    const advertIds = this.vehicles
      .filter(v => this.selectedVehicles.has(v.id)).map(v => v.advertId);

    const vehiclesToWithdraw = this.vehicleForModal
      ? [this.vehicleForModal.advertId]
      : advertIds;

    try {
      this.loading = true;
      await this.vehicleImportService.withdrawVehicles(vehiclesToWithdraw);
      this.selectedVehicles.clear();
      await this.loadVehicles();
    } catch (error) {
      console.error('Error withdrawing vehicles:', error);
    } finally {
      this.loading = false;
      this.closeModals();
    }
  }

  async republishAdvert(vehicle: InventAuctionLot): Promise<void> {
    try {
      this.loading = true;
      await this.advertService.publishAdvert(vehicle.advertId);
      await this.loadVehicles();
    } catch (error) {
      console.error('Error republishing vehicle:', error);
    } finally {
      this.loading = false;
    }
  }

  async withdrawVehicle(vehicle: InventAuctionLot): Promise<void> {
    try {
      this.loading = true;
      // await this.vehicleImportService.withdrawVehicle(vehicle.id);
      await this.loadVehicles();
    } catch (error) {
      console.error('Error withdrawing vehicle:', error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Open advert in new tab (for imported vehicles only)
   */
  openAdvertInNewTab(vehicle: InventAuctionLot): void {
    if (vehicle.advertId) {
      const advertUrl = this.urlService.advertView(vehicle.advertId, true);
      window.open(advertUrl, '_blank');
    }
  }

  /**
   * Check if the sale has ended (endDateTime < now)
   */
  isSaleEnded(vehicle: InventAuctionLot): boolean {
    if (!vehicle.endDateTime) {
      return false;
    }
    const now = new Date();
    const endDate = new Date(vehicle.endDateTime);
    return endDate < now;
  }

  updateUrl($event: any) {
    const activeTabIndex = this.uiTabs.getActiveElement().activeTabIndex;
    this.selectedTab = activeTabIndex;

    this.location.replaceState(this.adminURLService.adminImportManagement(activeTabIndex, true));

    this.setActiveTab(this.tabTypes[activeTabIndex]);
  }
}
